/**
 * @file HttpClientTransport — stateless typed wrapper around `fetch`.
 *
 * The transport is the seam between a workspace's typed `Client` (Account,
 * Vmm, Admin, …) and the wire. Picks up path-param interpolation,
 * query-string assembly, content negotiation, and Zod response validation;
 * keeps every consumer free of `fetch` boilerplate.
 *
 * Phase 2b of umbrella #518 promotes this class from `CoreClass` onto
 * `CoreTransport extends CoreLifecycle` so the operator drives configure
 * → start → stop uniformly with other lifecycle entities. The
 * dispatcher cache is torn down in `onStop` (closes the undici Agent)
 * to prevent socket leaks across daemon restarts in test fixtures.
 * The discovery-hop reconfigure path (admin awaits a back-end URL
 * refresh) lands as the typed `reconfigure(props)` method backed by
 * `CoreLifecycle.reconfigure` — legal in `configured` and `running`
 * states only.
 *
 * 401 dispatch is event-driven via the typed `HttpClientTransportEvents`
 * map: `transport.on('unauthorized', cb)` registers a multicast
 * listener that fires before the `HttpError` is thrown. One throwing
 * listener does not suppress the throw or starve siblings — the call
 * site iterates `listeners('unauthorized')` with per-handler
 * `try/catch`. Replaces the prior `setOnUnauthorized(cb)` slot-style
 * surface (umbrella #701 — `.on/.once/.off` idiom repo-wide).
 *
 * Phase 4 of umbrella #406. Replaces the surface today's `HttpClient`
 * (`core/frontend/services/http.ts`) exposes; that class stays
 * `@deprecated` until Phase 5 migrates `Account` / `Email` / `Vmm` /
 * `Claude` / admin / lab / www onto the new shape.
 */

import { z } from 'zod'
import {
  CoreError,
  CoreTransport,
  interpolate,
  metricNames,
  type Route,
  type RouteRequest,
  type RouteResponse,
  type TlsProps,
} from '../../shared/index.ts'
import {
  HttpError,
  NetworkError,
  TimeoutError,
  ValidationError,
} from '../errors.ts'

////////////////////////////////////////////////////////////////////////////////
// Outbound mTLS metric names
////////////////////////////////////////////////////////////////////////////////

const [M_CLIENT_HANDSHAKE_SUCCESS, M_CLIENT_HANDSHAKE_FAILURE] = metricNames(
  'mtls.client.handshake_success.total',
  'mtls.client.handshake_failure.total',
)

/**
 * Minimal undici.Dispatcher shape `fetch` accepts via its non-standard
 * `dispatcher` option in Node 22+. Declared locally so this file
 * doesn't depend on undici's types when bundled for browser builds —
 * the dynamic `import('undici')` in `buildDispatcher` is the only
 * place undici is named, and it never runs outside Node.
 */
type Dispatcher = {
  close?: () => Promise<void> | void
}

/**
 * True if this process is running under Node.js. Browsers and edge
 * workers return false. Used to skip the undici-based mTLS dispatcher
 * in environments where (a) undici isn't available and (b) browser
 * fetch can't carry client certs through a custom dispatcher anyway.
 */
function isNodeRuntime(): boolean {
  return (
    typeof process !== 'undefined' &&
    (process as { versions?: { node?: string } }).versions?.node != null
  )
}

/**
 * Build a Node `undici.Agent` configured with the supplied PEMs.
 * Returns `undefined` outside Node — browser / edge consumers ignore
 * the `tls` prop because browser fetch doesn't carry client
 * certificates through a custom dispatcher (client-cert auth in
 * browsers is OS-keystore-driven, not application-level). The
 * `undici` import is dynamic + `@vite-ignore`'d so SPA bundlers don't
 * tree-pull undici into the browser bundle.
 */
async function buildDispatcher(tls: TlsProps): Promise<Dispatcher | undefined> {
  if (!isNodeRuntime()) return undefined
  const { Agent } = await import(/* @vite-ignore */ 'undici')
  return new Agent({
    connect: {
      cert: tls.cert,
      // `.expose()` at the TLS handshake boundary — `tls.key` is a
      // `CoreSecret` so it can't escape via accidental serialization;
      // plaintext only crosses into undici's TLS connect here.
      key: tls.key.expose(),
      ca: tls.ca,
      // Intra-cluster mTLS authenticates peers via cert validation
      // (CA-signed leaf), not via hostname. Daemon SANs are role
      // tokens (`<role>.labcraft.internal`) and don't match the
      // loopback IP the dispatcher actually dials. The server still
      // validates the client cert against `ca`; role identity gets
      // recovered from the cert SAN via `deriveRoleFromCert` on the
      // server side. Trusting any CA-signed cert is the correct
      // semantics for intra-cluster RPC per #356.
      checkServerIdentity: () => undefined,
    },
  })
}

/**
 * True when an error looks like an mTLS handshake / connection
 * failure. Node's fetch surfaces TLS errors as a `TypeError` with a
 * `cause` field carrying the original `Error` whose `code` is one of
 * the `ERR_TLS_*` / `UNABLE_TO_*` / `CERT_*` codes.
 */
function isHandshakeError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const cause = (err as { cause?: { code?: unknown } }).cause
  const code = cause?.code ?? (err as { code?: unknown }).code
  if (typeof code !== 'string') return false
  return (
    code.startsWith('ERR_TLS_') ||
    code.startsWith('ERR_SSL_') ||
    code.startsWith('CERT_') ||
    code.startsWith('UNABLE_TO_') ||
    code === 'DEPTH_ZERO_SELF_SIGNED_CERT' ||
    code === 'SELF_SIGNED_CERT_IN_CHAIN' ||
    code === 'ECONNRESET'
  )
}

/** Extract a Prometheus-safe `code` label from an mTLS handshake error. */
function handshakeErrorCode(err: unknown): string {
  if (!err || typeof err !== 'object') return 'unknown'
  const cause = (err as { cause?: { code?: unknown } }).cause
  const raw = cause?.code ?? (err as { code?: unknown }).code
  if (typeof raw !== 'string') return 'unknown'
  return raw.toLowerCase().replace(/[^a-z0-9_]/g, '_')
}

////////////////////////////////////////////////////////////////////////////////
// Types
////////////////////////////////////////////////////////////////////////////////

/**
 * Constructor / `configure(...)` props for `HttpClientTransport`.
 *
 * @property id - CoreClass id (default `'http'`).
 * @property baseUrl - Base URL prepended to every route path. Trailing
 *   slash is stripped on assignment so callers don't have to think
 *   about it.
 * @property timeout - Per-request timeout in milliseconds. Default 30s
 *   (matches today's `HttpClient`).
 * @property credentials - Forwarded to `fetch`'s `credentials` option.
 *   Default `'same-origin'`.
 * @property headers - Default headers merged into every request.
 *   `Content-Type: application/json` and `Accept: application/json` are
 *   layered on top (caller cannot accidentally strip them).
 * @property tls - mTLS cert/key/CA the *caller* (this transport's
 *   owning daemon) presents on outbound requests. Plumbed into
 *   Node's `fetch` via an `undici.Agent` dispatcher; the dispatcher
 *   is lazy-built on first request and cached. Node-only — browser /
 *   edge consumers ignore the prop (browser fetch can't carry client
 *   certs through a custom dispatcher; client-cert auth in browsers
 *   is OS-keystore-driven). Optional so the same transport class
 *   works for both intra-cluster mTLS RPC and SPA-to-public-edge
 *   plain HTTPS.
 */
export type HttpClientTransportProps = {
  id?: string
  baseUrl: string
  timeout?: number
  credentials?: RequestCredentials
  headers?: Record<string, string>
  tls?: TlsProps
}

/**
 * Per-call arguments for `request(...)` / `stream(...)`. All fields
 * optional so static-path GET routes (the common case) write
 * `transport.request(MyRoute)` with no second argument.
 *
 * `timeout` overrides the instance-level default for a single call
 * — used by long-running endpoints (e.g. lab's chapter-fetch waiting
 * on cold hypervisor boot, observed ≥ 40 s on a Mac mini). Absent →
 * use the transport's `this.timeout`. The override does not mutate
 * instance state — concurrent unrelated calls keep the default.
 *
 * `keepalive` maps to the fetch `keepalive` flag: set it on a
 * fire-and-forget request issued from a `pagehide` / `visibilitychange`
 * handler so the browser finishes it after the document unloads (e.g.
 * lab's engagement-release beacon). Platform-bounded to a small body;
 * fine for empty/small payloads. Absent → normal (cancel-on-unload).
 *
 * `headers` are per-call headers merged over the transport's default
 * headers (and under the fixed `Content-Type` / `Accept`). Used for
 * one-off request metadata a route body can't carry — e.g. Resend's
 * `Idempotency-Key` on an outbox send. The override does not mutate
 * instance state.
 */
export type RequestArgs<R extends Route> = {
  params?: Record<string, string>
  query?: Record<string, string | number | boolean | undefined>
  body?: RouteRequest<R>
  timeout?: number
  keepalive?: boolean
  headers?: Record<string, string>
}

////////////////////////////////////////////////////////////////////////////////
// HttpClientTransport
////////////////////////////////////////////////////////////////////////////////

/**
 * Stateless HTTP transport. Consumed by workspace Client classes
 * (`Account`, `Vmm`, `Admin`, …) via constructor injection.
 *
 * @example
 * ```typescript
 * const http = new HttpClientTransport({ baseUrl: 'http://127.0.0.1:24100' })
 * const learner = await http.request(GetLearnerRoute, { params: { id: '…' } })
 * ```
 */
/**
 * Typed event map for `HttpClientTransport`.
 *
 * @property unauthorized - Fires on every 401 response before the
 *   resulting `HttpError` is thrown. Multicast: register N listeners
 *   via `.on('unauthorized', cb)`. Replaces the prior
 *   `setOnUnauthorized(cb)` slot-style API.
 */
export type HttpClientTransportEvents = {
  unauthorized: []
}

export class HttpClientTransport extends CoreTransport<
  HttpClientTransportProps,
  HttpClientTransportEvents
> {
  //////////////////////////////////////////////////////////////////////////////
  // Instance Properties
  //////////////////////////////////////////////////////////////////////////////

  private baseUrl: string
  private timeout: number
  private credentials: RequestCredentials
  private headers: Record<string, string>
  private tls: TlsProps | null = null
  /**
   * Lazily-initialised undici dispatcher. Populated on first request
   * when `tls` is set; `null` until then. Browser consumers leave it
   * `null` forever — `buildDispatcher` returns undefined off-Node.
   *
   * Closed in `onStop` to prevent socket leaks across daemon restarts
   * in test fixtures.
   */
  private dispatcher: Dispatcher | null = null
  /**
   * AbortControllers for every in-flight fetch. Aborted en-masse in
   * `onStop` so a stop signal (SIGINT in lc-dev, daemon shutdown in
   * tests) doesn't block waiting for a long per-request timeout to
   * elapse. Membership is bounded by request concurrency, not the
   * timeout, so the Set never grows unboundedly.
   */
  private inFlight: Set<AbortController> = new Set()

  //////////////////////////////////////////////////////////////////////////////
  // Constructor
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Synchronous construction. Validates `baseUrl` at call time so a
   * misconfigured daemon fails loud at boot, not at first request.
   * Stays in `created` state — the lifecycle (`configure()` → `start()`)
   * is driven by the adopting parent's child-walk.
   */
  constructor(props: HttpClientTransportProps) {
    super({ ...props, id: props.id ?? 'http' })
    this.baseUrl = ''
    this.timeout = 30_000
    this.credentials = 'same-origin'
    this.headers = {}
    this.applyProps(props)
  }

  //////////////////////////////////////////////////////////////////////////////
  // Public Methods - toJSON
  //////////////////////////////////////////////////////////////////////////////

  override toJSON(): Record<string, unknown> {
    return {
      ...super.toJSON(),
      baseUrl: this.baseUrl,
      timeout: this.timeout,
      tls: this.tls !== null,
      inFlight: this.inFlight.size,
    }
  }

  //////////////////////////////////////////////////////////////////////////////
  // Public Methods - Reconfigure
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Update transport configuration on a live entity. Used for the
   * discovery-hop pattern where admin awaits a back-end URL refresh
   * after start (the daemon binds before the upstream URL is final).
   *
   * Applies new fields synchronously (URL validation, dispatcher
   * cache clear on `tls` toggle), then re-runs `onConfigure` via
   * `CoreLifecycle.reconfigure`. Legal in `configured` and `running`
   * states only.
   */
  async reconfigure(props: Partial<HttpClientTransportProps>): Promise<void> {
    this.applyProps(props)
    await this.runReconfigure()
  }

  //////////////////////////////////////////////////////////////////////////////
  // Lifecycle Hooks
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Tear down the cached undici dispatcher (if any) so its socket
   * pool doesn't outlive the test fixture. No-op when off-Node or
   * when no request ever populated the cache.
   */
  protected override async onStop(): Promise<void> {
    // Abort in-flight fetches first so undici's `dispatcher.close()`
    // doesn't wait for the longest per-request timeout to elapse —
    // critical in lc-dev where Ctrl-C should exit promptly even if a
    // long-timeout RPC (e.g. lab→hypervisor cold-boot, 120 s) is mid-
    // flight.
    for (const controller of this.inFlight) {
      try {
        controller.abort()
      } catch {
        /* idempotent — ignore */
      }
    }
    this.inFlight.clear()
    if (this.dispatcher?.close) {
      await this.dispatcher.close()
    }
    this.dispatcher = null
  }

  //////////////////////////////////////////////////////////////////////////////
  // Private Methods - props handling
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Apply props to instance fields. Shared by the constructor (full
   * props) and `reconfigure` (partial props). URL validation throws
   * `INVALID_CONFIG` synchronously on bad input. Toggling `tls`
   * clears the cached dispatcher so the next request rebuilds
   * against the new bundle.
   */
  private applyProps(props: Partial<HttpClientTransportProps>): void {
    if (props.baseUrl !== undefined) {
      try {
        void new URL(props.baseUrl)
      } catch {
        throw new CoreError({
          code: 'INVALID_CONFIG',
          message: `Invalid baseUrl: ${props.baseUrl}`,
          metadata: { baseUrl: props.baseUrl },
        })
      }
      this.baseUrl = props.baseUrl.replace(/\/$/, '')
    }
    if (props.timeout !== undefined) this.timeout = props.timeout
    if (props.credentials !== undefined) this.credentials = props.credentials
    if (props.headers !== undefined) this.headers = props.headers
    if (props.tls !== undefined) {
      this.tls = props.tls
      this.dispatcher = null
    }
  }

  //////////////////////////////////////////////////////////////////////////////
  // Public Methods - Request
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Make a type-safe HTTP request.
   *
   * Path-param interpolation runs through `interpolate(...)` (shared
   * since Phase 1). Query params are URL-encoded; `undefined` values
   * drop the key. Response is `safeParse`'d against the route's
   * `response` schema and surfaced as `RouteResponse<R>`.
   *
   * @throws HttpError on `response.status >= 400`.
   * @throws NetworkError on transport failures.
   * @throws TimeoutError when the request exceeds `timeout`.
   * @throws ValidationError when the response body fails schema parse.
   */
  async request<R extends Route>(
    route: R,
    args: RequestArgs<R> = {},
  ): Promise<RouteResponse<R>> {
    const url = this.buildUrl(route.path, args.params, args.query)
    const start = Date.now()
    const effectiveTimeout = args.timeout ?? this.timeout
    this.debug('request', { method: route.method, path: route.path })

    try {
      const response = await this.doFetch(
        url,
        {
          method: route.method,
          body: args.body !== undefined ? JSON.stringify(args.body) : undefined,
          ...(args.keepalive !== undefined
            ? { keepalive: args.keepalive }
            : {}),
          ...(args.headers !== undefined ? { headers: args.headers } : {}),
        },
        effectiveTimeout,
      )

      if (!response.ok) {
        const text = await response.text().catch(() => '')
        if (response.status === 401) {
          // Isolate listeners so one misbehaving subscriber can't
          // suppress the throw below or starve its siblings — `emit`
          // doesn't wrap each call itself. Drive the listener set
          // directly via the typed `listeners()` accessor.
          for (const handler of this.listeners('unauthorized')) {
            try {
              ;(handler as () => void)()
            } catch (cbErr) {
              this.warn('onUnauthorized', CoreError.from(cbErr))
            }
          }
        }
        throw new HttpError(response.status, text, url)
      }

      // 204 No Content (or any genuinely empty body) — surface as
      // `undefined`. Routes declared with `z.void()` are happy with
      // that; routes that declare a real response schema fall through
      // to `json()` and `safeParse` rejects empty input loudly.
      const contentLength = response.headers.get('content-length')
      if (response.status === 204 || contentLength === '0') {
        return undefined as RouteResponse<R>
      }

      const data = await response.json()
      const parsed = route.response.safeParse(data)
      if (!parsed.success) {
        throw new ValidationError(
          'Response validation failed',
          parsed.error.issues,
        )
      }

      this.info('request', {
        method: route.method,
        path: route.path,
        status: response.status,
        latency: Date.now() - start,
      })
      return parsed.data as RouteResponse<R>
    } catch (err) {
      if (err instanceof HttpError || err instanceof ValidationError) throw err
      if (err instanceof TypeError && err.message.includes('fetch')) {
        throw new NetworkError(err)
      }
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new TimeoutError(effectiveTimeout)
      }
      throw new NetworkError(err instanceof Error ? err : undefined)
    }
  }

  //////////////////////////////////////////////////////////////////////////////
  // Public Methods - Stream (Server-Sent Events)
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Open an SSE stream to a `stream: true` route. Each `data:` event is
   * `safeParse`'d against the route's `response` schema and yielded as
   * `RouteResponse<R>`. Per umbrella Q3 the return type is
   * `AsyncIterable<T>` (not a `Subscription<T>`); cancellation comes
   * from `break`-ing the `for await` loop, which closes the underlying
   * response stream.
   *
   * `event:` lines are ignored — Phase 4 doesn't model named events;
   * only the default-event channel survives. Lines beginning with `:`
   * are SSE comments (heartbeats, often) and dropped.
   *
   * @yields Each successfully-parsed event payload.
   * @throws HttpError when the upgrade response is `>= 400`.
   */
  async *stream<R extends Route & { stream: true }>(
    route: R,
    args: RequestArgs<R> = {},
  ): AsyncIterable<RouteResponse<R>> {
    const url = this.buildUrl(route.path, args.params, args.query)
    const dispatcher = await this.getDispatcher()
    const init: RequestInit & { dispatcher?: Dispatcher } = {
      method: route.method,
      credentials: this.credentials,
      headers: {
        ...this.headers,
        Accept: 'text/event-stream',
        ...(args.body !== undefined
          ? { 'Content-Type': 'application/json' }
          : {}),
      },
      body: args.body !== undefined ? JSON.stringify(args.body) : undefined,
    }
    if (dispatcher) init.dispatcher = dispatcher
    const response = await fetch(url, init)

    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new HttpError(response.status, text)
    }
    if (!response.body) {
      throw new CoreError({
        code: 'STREAM_NO_BODY',
        message: 'SSE response had no body',
      })
    }

    // Decode the byte stream into UTF-8 chunks. The SSE event boundary
    // is a blank line (`\n\n` after stripping `\r`); we buffer chunks
    // and emit each complete event.
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        let boundary = buffer.indexOf('\n\n')
        while (boundary !== -1) {
          const event = buffer.slice(0, boundary)
          buffer = buffer.slice(boundary + 2)
          const payload = parseSseEvent(event)
          if (payload === undefined) {
            boundary = buffer.indexOf('\n\n')
            continue
          }
          const parsed = (route.response as z.ZodType).safeParse(payload)
          if (parsed.success) {
            yield parsed.data as RouteResponse<R>
          } else {
            this.warn(
              'stream.parse',
              new ValidationError(
                'SSE event validation failed',
                parsed.error.issues,
              ),
            )
          }
          boundary = buffer.indexOf('\n\n')
        }
      }
    } finally {
      // Release the reader so the underlying socket can be reused.
      reader.releaseLock()
    }
  }

  //////////////////////////////////////////////////////////////////////////////
  // Private Methods
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Compose the full URL for a request: baseUrl + interpolated path +
   * query string. `interpolate` lives in `core/shared` since Phase 1.
   */
  private buildUrl(
    path: string,
    params?: Record<string, string>,
    query?: Record<string, string | number | boolean | undefined>,
  ): string {
    const interpolated = params ? interpolate(path, params) : path
    const qs = buildQueryString(query)
    return `${this.baseUrl}${interpolated}${qs}`
  }

  /**
   * `fetch` with timeout, optionally routed through the mTLS
   * dispatcher. Wraps the AbortController dance so callers stay free
   * of the boilerplate.
   *
   * Node's fetch (undici-backed) accepts a non-standard `dispatcher`
   * option; browser fetch silently ignores unknown options. So this
   * path is safe on both runtimes — `getDispatcher()` only returns a
   * dispatcher on Node when `tls` was supplied, and SPA bundlers
   * never load the undici code thanks to the dynamic `@vite-ignore`'d
   * import.
   *
   * mTLS handshake outcomes are observable via
   * `mtls.client.handshake_success.total` and
   * `mtls.client.handshake_failure.total{code}`.
   */
  private async doFetch(
    url: string,
    init: RequestInit,
    timeoutMs: number = this.timeout,
  ): Promise<Response> {
    const controller = new AbortController()
    this.inFlight.add(controller)
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    const dispatcher = await this.getDispatcher()
    const initWithDispatcher: RequestInit & { dispatcher?: Dispatcher } = {
      ...init,
      credentials: this.credentials,
      headers: {
        // Per-call headers override the transport defaults but NOT the fixed
        // JSON content-type / accept — those are applied last so a caller
        // cannot clobber them (matches the `RequestArgs.headers` contract).
        ...this.headers,
        ...(init.headers as Record<string, string> | undefined),
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      signal: controller.signal,
    }
    if (dispatcher) initWithDispatcher.dispatcher = dispatcher

    try {
      const response = await fetch(url, initWithDispatcher)
      if (this.tls) this.counter(M_CLIENT_HANDSHAKE_SUCCESS)
      return response
    } catch (err) {
      if (this.tls && isHandshakeError(err)) {
        const code = handshakeErrorCode(err)
        this.counter(M_CLIENT_HANDSHAKE_FAILURE, { code })
      }
      throw err
    } finally {
      clearTimeout(timeoutId)
      this.inFlight.delete(controller)
    }
  }

  /**
   * Resolve the lazy mTLS dispatcher. Returns the cached value if it
   * exists; otherwise builds it via `buildDispatcher` and caches the
   * result. Returns `undefined` when `tls` isn't set or when off-Node
   * (browser / edge consumers fall through to default fetch).
   */
  private async getDispatcher(): Promise<Dispatcher | undefined> {
    if (!this.tls) return undefined
    if (this.dispatcher) return this.dispatcher
    const built = await buildDispatcher(this.tls)
    if (!built) return undefined
    this.dispatcher = built
    return built
  }
}

////////////////////////////////////////////////////////////////////////////////
// Module-local helpers
////////////////////////////////////////////////////////////////////////////////

/**
 * Build a `?k=v&k2=v2` query string from a record. `undefined` values
 * drop the key entirely so callers can write
 * `query: { include: maybeInclude }` without conditionals.
 *
 * Returns the empty string when no query params yield a value.
 */
function buildQueryString(
  query: Record<string, string | number | boolean | undefined> | undefined,
): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined) continue
    params.append(k, String(v))
  }
  const qs = params.toString()
  return qs.length > 0 ? `?${qs}` : ''
}

/**
 * Parse a single SSE event chunk into its `data:` payload. Returns
 * `undefined` when the chunk has no `data:` field (e.g. a pure comment
 * or a name-only `event:` chunk). Multi-line `data:` per spec
 * concatenates with `\n` between lines; we JSON-parse the result if it
 * looks like JSON, otherwise pass through as a string.
 */
function parseSseEvent(chunk: string): unknown {
  const dataLines: string[] = []
  for (const rawLine of chunk.split('\n')) {
    const line = rawLine.replace(/\r$/, '')
    if (line.startsWith(':')) continue
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /, ''))
    }
  }
  if (dataLines.length === 0) return undefined
  const joined = dataLines.join('\n')
  try {
    return JSON.parse(joined)
  } catch {
    return joined
  }
}
